import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNotifications } from 'modules/notification';
import { useDealBuilderStepNavigation } from './useDealBuilderStepNavigation';
import { getStepIndex } from '../lib';
import { DealBuilderFormValues, DealBuilderStep } from '../types';
import { useDealBuilderContext } from '../context/DealBuilderContext';

const useContactsValidation = () => {
  const { getValues } = useFormContext<DealBuilderFormValues>();
  const { showNotification } = useNotifications();

  return useCallback((): boolean => {
    const { ownerPgIds, accountContactIds } = getValues('contacts');
    if (ownerPgIds.length === 0 && accountContactIds.length === 0) {
      showNotification('Select at least one contact to continue', { type: 'warning' });
      return false;
    }
    return true;
  }, [getValues, showNotification]);
};

const useEquipmentValidation = () => {
  const { selectedEquipmentItem } = useDealBuilderContext();
  const { showNotification } = useNotifications();

  return useCallback((): boolean => {
    if (!selectedEquipmentItem) {
      showNotification('Select at least one equipment unit to continue', { type: 'warning' });
      return false;
    }
    const { details, price } = selectedEquipmentItem.equipment;
    const missing: string[] = [];
    if (!details.vin) missing.push('VIN/Serial');
    if (!details.year) missing.push('Year');
    if (!details.make) missing.push('Make');
    if (!details.model) missing.push('Model');
    if (!price.retailPrice && !price.wholesaleCost) missing.push('Price');
    if (missing.length > 0) {
      showNotification(`Equipment is missing required fields: ${missing.join(', ')}`, { type: 'warning' });
      return false;
    }
    return true;
  }, [selectedEquipmentItem, showNotification]);
};

export const useValidateAndAdvance = () => {
  const { trigger } = useFormContext<DealBuilderFormValues>();
  const { viewStep, advanceToNextStep } = useDealBuilderStepNavigation();
  const validateContacts = useContactsValidation();
  const validateEquipment = useEquipmentValidation();

  return useCallback(async () => {
    const isValid = await trigger(viewStep);
    if (!isValid) return;
    if (viewStep === DealBuilderStep.Contacts && !validateContacts()) return;
    if (viewStep === DealBuilderStep.Equipment && !validateEquipment()) return;
    advanceToNextStep();
  }, [viewStep, trigger, advanceToNextStep, validateContacts, validateEquipment]);
};

export const useValidatedGoToStep = () => {
  const { viewStep, goToStep } = useDealBuilderStepNavigation();
  const validateContacts = useContactsValidation();
  const validateEquipment = useEquipmentValidation();

  return useCallback(
    (targetStep: DealBuilderStep) => {
      const targetIndex = getStepIndex(targetStep);
      if (
        viewStep === DealBuilderStep.Contacts &&
        targetIndex > getStepIndex(DealBuilderStep.Contacts) &&
        !validateContacts()
      ) {
        return;
      }
      if (
        viewStep === DealBuilderStep.Equipment &&
        targetIndex > getStepIndex(DealBuilderStep.Equipment) &&
        !validateEquipment()
      ) {
        return;
      }
      goToStep(targetStep);
    },
    [viewStep, goToStep, validateContacts, validateEquipment]
  );
};
